import { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { updateMyBetaFeatures } from '../services/userService'
import { useAuth } from './AuthContext'

const UserContext = createContext()

export function UserProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const { user: authUser, loading: authLoading } = useAuth()

  // Fetch user profile from backend.
  //
  // DUB-75: depend on authUser?.id rather than the authUser object reference.
  // AuthContext used to call setUser on every TOKEN_REFRESHED, which produced
  // a fresh user-object reference even when identity was unchanged — that
  // cascaded a "loading=true" state here, unmounting the route under
  // ProtectedRoute's spinner and wiping unsaved bracket-editor state on
  // remount. The AuthContext fix already prevents this, but keying on the
  // primitive id is a defense-in-depth: if a future change re-introduces
  // a redundant setUser call, this effect won't re-run for the same user.
  const authUserId = authUser?.id ?? null
  useEffect(() => {
    if (authLoading) {
      setLoading(true)
      return
    }

    if (authUserId) {
      setLoading(true)
      fetchUserProfile()
    } else {
      // No auth user - clear user profile and stop loading
      setUser(null)
      setLoading(false)
    }
  }, [authUserId, authLoading])

  const fetchUserProfile = async () => {
    try {
      setLoading(true)
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        setUser(null)
        setLoading(false)
        return
      }

      // Authoritative role lives in auth.users.raw_app_meta_data, kept in sync via the
      // sync_user_role_to_app_metadata trigger. Reading it from session.user.app_metadata
      // means we trust the signed JWT instead of an unauthenticated profile fetch — and
      // it's available before get_current_user_profile resolves.
      const sessionRole = session.user?.app_metadata?.role || null

      const { data, error } = await supabase.rpc('get_current_user_profile')
      if (error) throw error
      if (!data) {
        setUser(null)
        return
      }

      setUser({
        ...data,
        role: sessionRole || data.role || 'user',
        status: data.status || 'active',
        beta_features_enabled: data.beta_features_enabled === true,
        managedGroups: data.managedGroups || []
      })
    } catch (err) {
      console.error('Error fetching user profile:', err)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const promoteToGroupManager = (groupId) => {
    setUser(prev => ({
      ...prev,
      role: 'group_manager',
      managedGroups: [...(prev?.managedGroups || []), groupId]
    }))
  }

  const removeGroupFromManager = (groupId) => {
    setUser(prev => {
      const currentManagedGroups = prev?.managedGroups || []
      const updatedManagedGroups = currentManagedGroups.filter(id => id !== groupId)
      return {
        ...prev,
        managedGroups: updatedManagedGroups,
        // Demote to user if no longer managing any groups
        role: updatedManagedGroups.length === 0 && prev.role === 'group_manager' ? 'user' : prev.role
      }
    })
  }

  const isGroupManager = (groupId) => {
    return (user?.managedGroups || []).includes(groupId)
  }

  const setBetaFeaturesEnabled = async (value) => {
    const next = !!value
    setUser(prev => prev ? { ...prev, beta_features_enabled: next } : prev)
    try {
      await updateMyBetaFeatures(next)
    } catch (err) {
      setUser(prev => prev ? { ...prev, beta_features_enabled: !next } : prev)
      throw err
    }
  }

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      loading,
      fetchUserProfile,
      promoteToGroupManager,
      removeGroupFromManager,
      isGroupManager,
      betaFeaturesEnabled: user?.beta_features_enabled === true,
      setBetaFeaturesEnabled,
    }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
